import { ApiError, apiFetch } from "../../api/client";
import type {
  AdminAttachmentSummary,
  AdminAttachmentUploadRequest,
  AdminTemplateContentRequest,
  AdminTemplateCreateRequest,
  AdminTemplateOption,
  AdminTemplatePreview,
  AdminTemplateSummary,
  AdminTemplateWriteResult,
} from "../../api/types";

/** `GET admin/templates` (role Consulta). */
export function listTemplates(): Promise<AdminTemplateSummary[]> {
  return apiFetch<AdminTemplateSummary[]>("/panel/templates");
}

/**
 * `GET admin/template-options` (role Consulta) — bounded (`size<=50`) selector read for the
 * campaign editor's template selector (redesign-panel-hubspot-mailgun, PR 9, task 9.1/9.2/9.3).
 * Never exposes `rutaBlob`. The default size (50, server-clamped) is enough to cover this
 * tenant's realistic scale in one call — fetched once per editor mount, never per keystroke.
 */
export function getTemplateOptions(): Promise<AdminTemplateOption[]> {
  return apiFetch<AdminTemplateOption[]>("/panel/template-options");
}

/** `GET admin/templates/{id}/preview` (role Consulta). Rendered HTML of the active blob. */
export function getTemplatePreview(id: string): Promise<AdminTemplatePreview> {
  return apiFetch<AdminTemplatePreview>(`/panel/templates/${id}/preview`);
}

/** `POST admin/templates` (role Operador). 201 on success; 400 invalid Liquid; 413 HTML over 1MB. */
export function createTemplate(body: AdminTemplateCreateRequest): Promise<AdminTemplateWriteResult> {
  return apiFetch<AdminTemplateWriteResult>("/panel/templates", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * `PUT admin/templates/{id}/content` (role Operador) — content-only route (design.md A6).
 * 409 when a `programada`/`enviando` campaign references the template (design.md D2).
 */
export function replaceTemplateContent(id: string, body: AdminTemplateContentRequest): Promise<AdminTemplateWriteResult> {
  return apiFetch<AdminTemplateWriteResult>(`/panel/templates/${id}/content`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

/** `DELETE admin/templates/{id}` (role Operador). Always soft-delete server-side (design.md A7) — never a physical delete. */
export function softDeleteTemplate(id: string): Promise<void> {
  return apiFetch<void>(`/panel/templates/${id}`, {
    method: "DELETE",
  });
}

/** `GET panel/templates/{id}/attachments` (role Consulta). All active attachments, ordered by `orden`. */
export function listAttachments(templateId: string): Promise<AdminAttachmentSummary[]> {
  return apiFetch<AdminAttachmentSummary[]>(`/panel/templates/${templateId}/attachments`);
}

/**
 * `POST panel/templates/{id}/attachments` (role Operador). 201 on success; 400/415 invalid
 * file; 409 blocked by a scheduled/sending campaign; 413 aggregate size over the 30 MB
 * post-Base64 ceiling (design.md D2, `AdminAttachmentPolicy`). The 413 case is remapped to
 * an explicit, size-specific message (task 4.4) — the raw server text ("el agregado de
 * adjuntos... supera el limite permitido") never names the 30 MB ceiling on its own.
 */
export async function uploadAttachment(
  templateId: string,
  body: AdminAttachmentUploadRequest,
): Promise<AdminAttachmentSummary> {
  try {
    return await apiFetch<AdminAttachmentSummary>(`/panel/templates/${templateId}/attachments`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  } catch (error) {
    if (error instanceof ApiError && error.status === 413) {
      throw new ApiError(413, "El agregado de adjuntos de la plantilla supera el limite de 30 MB permitido por correo.");
    }
    throw error;
  }
}

/** `DELETE panel/templates/{id}/attachments/{attachmentId}` (role Operador). 409 same check as upload (design.md A7). */
export function deleteAttachment(templateId: string, attachmentId: string): Promise<void> {
  return apiFetch<void>(`/panel/templates/${templateId}/attachments/${attachmentId}`, {
    method: "DELETE",
  });
}
